import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useMutation } from "@apollo/client";
import Select from "react-select";
import { toast } from "react-toastify";
import { COPY_TENANT_CONFIGURATION_MUTATION } from "../../../graphql/mutations";

const CopyTenantConfigurationModal = ({ show, sourceTenant, tenants, handleClose, handleEnvChange, env }) => {
  const [copyTenantConfiguration, { loading: mutationLoading }] = useMutation(COPY_TENANT_CONFIGURATION_MUTATION);
  const [targetTenantId, setTargetTenantId] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!targetTenantId) {
      toast.error("Please select a target tenant.");
      return;
    }

    if (targetTenantId === sourceTenant.id) {
      toast.error("Source and target tenants cannot be the same.");
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmCopy = async () => {
    setShowConfirmation(false);

    try {
      const { data } = await copyTenantConfiguration({
        variables: {
          sourceTenantId: sourceTenant.id,
          targetTenantId: targetTenantId
        }
      });

      if (data?.copyTenantConfiguration) {
        setTimeout(() => {
          toast.success(
            `Tenant configuration copied successfully from ${sourceTenant.name} to ${tenants.find((t) => t.id === targetTenantId)?.name || targetTenantId}`
          );
          handleEnvChange(env);
        }, 200);
        handleClose();
      } else {
        toast.error("Failed to copy tenant configuration.");
      }
    } catch (err) {
      setTimeout(() => {
        toast.error(`Failed to copy tenant configuration: ${err.message}`);
      }, 200);
    }
  };

  const handleCloseModal = () => {
    setTargetTenantId("");
    setShowConfirmation(false);
    handleClose();
  };

  // Filter out the source tenant from the list
  const availableTenants = tenants?.filter((tenant) => tenant.id !== sourceTenant?.id) || [];

  const tenantOptions = availableTenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name
  }));

  return (
    <>
      <Modal show={show} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copy Tenant Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit} id="copyTenantForm">
            <Form.Group className="mb-3">
              <Form.Label>Source Tenant</Form.Label>
              <Form.Control type="text" value={sourceTenant?.name || ""} disabled />
              <Form.Text className="text-muted">Configuration will be copied from this tenant</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Target Tenant <span className="text-danger">*</span>
              </Form.Label>
              <Select
                options={tenantOptions}
                onChange={(selectedOption) => setTargetTenantId(selectedOption?.value || "")}
                value={tenantOptions.find((option) => option.value === targetTenantId) || null}
                placeholder="Search and select a target tenant..."
                isClearable
                isSearchable
              />
              <Form.Text className="text-muted">
                <strong>Warning:</strong> This will completely replace the target tenant's configuration.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="copyTenantForm" disabled={mutationLoading || !targetTenantId}>
            {mutationLoading ? "Copying configuration..." : "Copy Configuration"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Dialog */}
      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Copy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to copy the configuration from <strong>{sourceTenant?.name}</strong> to{" "}
            <strong>{tenants?.find((t) => t.id === targetTenantId)?.name}</strong>?
          </p>
          <p className="text-danger mb-0">
            <strong>Warning:</strong> This will completely overwrite the target tenant's configuration and cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleConfirmCopy} disabled={mutationLoading}>
            {mutationLoading ? "Copying..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export { CopyTenantConfigurationModal };
